import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { SignatureInput, exportDrawn, renderTyped } from './signatureTools';
import PortalShell, { PortalPanel as Panel, PORTAL } from '../../components/public/PortalShell';

const TOKEN_KEY = 'agriware-sign-token';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Keep the token out of the address bar (and screenshots / history), but survive a refresh.
function useSigningToken() {
  const { token: urlToken } = useParams();
  const [token] = useState(() => {
    if (urlToken) {
      try { sessionStorage.setItem(TOKEN_KEY, urlToken); } catch {}
      return urlToken;
    }
    try { return sessionStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  useEffect(() => {
    if (urlToken) window.history.replaceState(null, '', '/sign');
  }, [urlToken]);
  return token;
}

async function post(path, body) {
  const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return res;
}

export default function Sign() {
  const token = useSigningToken();
  const [state, setState] = useState({ phase: 'loading' });

  useEffect(() => {
    if (!token) { setState({ phase: 'blocked', code: 'invalid', message: 'This signing link is not valid.' }); return; }
    post('/api/sign/request', { token })
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (res.ok) setState({ phase: 'ready', req: body });
        else setState({ phase: 'blocked', ...body });
      })
      .catch(() => setState({ phase: 'blocked', code: 'error', message: 'Could not reach the server. Check your connection and try again.' }));
  }, [token]);

  const tone = state.phase === 'done' || state.code === 'already_signed' ? 'success'
    : state.phase === 'blocked' && state.code !== 'not_your_turn' ? 'danger' : 'neutral';

  return (
    <PortalShell maxWidth={860} tone={tone} footer="Agriware Ltd · Secure electronic signing under the Electronic Communications Act 2000">
      <div>
        {state.phase === 'loading' && <Panel><div style={{ color: 'var(--color-text-tertiary)', fontSize: 13 }}>Loading...</div></Panel>}
        {state.phase === 'blocked' && <Blocked {...state} />}
        {state.phase === 'ready' && <SignForm token={token} req={state.req} onDone={(completed, payUrl) => {
          try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
          setState({ phase: 'done', req: state.req, completed, payUrl });
        }} />}
        {state.phase === 'done' && (
          <Panel>
            <div style={{ fontSize: 20, marginBottom: 10 }}>Thank you, your signature is recorded</div>
            <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              You have signed <strong>{state.req.title}</strong>.{' '}
              {state.completed
                ? 'All parties have now signed. The completed document is being emailed to you.'
                : 'You will receive the completed document by email when all parties have signed.'}
            </div>
            {state.payUrl ? (
              <div style={{ marginTop: 20, borderTop: '0.5px solid var(--color-border-light)', paddingTop: 16 }}>
                <div style={{ fontSize: 16, marginBottom: 6 }}>Next step: payment</div>
                <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 14 }}>{state.req.payment?.summary}. We have also emailed you this payment link.</div>
                <a href={state.payUrl} style={{ display: 'block', textAlign: 'center', lineHeight: '46px', height: 46, borderRadius: 'var(--radius-md)', fontSize: 15, background: PORTAL.orange, color: '#fff', textDecoration: 'none' }}>Continue to payment</a>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 14 }}>You can close this page.</div>
            )}
          </Panel>
        )}
      </div>
    </PortalShell>
  );
}

const BLOCK_TITLES = {
  invalid: 'Link not valid',
  voided: 'Contract withdrawn',
  expired: 'Link expired',
  already_signed: 'Already signed',
  not_your_turn: 'Not ready for your signature yet',
  unavailable: 'Not available',
  error: 'Something went wrong',
};

function Blocked({ code, message, title }) {
  return (
    <Panel>
      <div style={{ fontSize: 20, marginBottom: 10 }}>{BLOCK_TITLES[code] || BLOCK_TITLES.error}</div>
      {title && <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>{title}</div>}
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{message || 'This link cannot be used.'}</div>
      {code === 'not_your_turn' && <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 12 }}>You will receive an email when it is your turn to sign.</div>}
      {(code === 'invalid' || code === 'expired') && <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginTop: 12 }}>If you still need to sign, contact Agriware for a new link.</div>}
    </Panel>
  );
}

function SignForm({ token, req, onDone }) {
  const [reachedEnd, setReachedEnd] = useState(false);
  const [ackRead, setAckRead] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState('draw');
  const [typedSig, setTypedSig] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const padRef = useRef(null);

  const readOk = reachedEnd || ackRead;
  const sigOk = mode === 'draw' ? hasDrawn : typedSig.trim().length > 0;
  const canSubmit = readOk && name.trim() && EMAIL_RE.test(email.trim()) && sigOk && consent && !submitting;

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const signaturePng = mode === 'draw' ? exportDrawn(padRef.current) : renderTyped(typedSig.trim());
      const res = await post('/api/sign/submit', {
        token, typedName: name.trim(), email: email.trim(), signaturePng,
        signatureMethod: mode === 'draw' ? 'drawn' : 'typed', consent: true,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Your signature could not be recorded. Please try again.');
      onDone(body.completed, body.payUrl || null);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel>
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{req.clientName}</div>
        <div style={{ fontSize: 22, margin: '4px 0 10px' }}>{req.title}</div>
        <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
          You are signing as <strong>{req.signer.name}</strong>, {req.signer.role}
        </div>
        {req.payment && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            <span style={{ color: 'var(--color-text-tertiary)' }}>Payment: </span>{req.payment.summary}
            {req.payment.isPayer && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>You will be taken to secure payment after signing.</div>}
          </div>
        )}
        {req.others.length > 0 && (
          <div style={{ marginTop: 14, borderTop: '0.5px solid var(--color-border-light)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>Also signing</div>
            {req.others.map((o, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '4px 0' }}>
                <span>{o.name} <span style={{ color: 'var(--color-text-tertiary)' }}>· {o.role}</span></span>
                <span style={{ color: o.signed ? 'var(--color-success)' : 'var(--color-text-tertiary)', whiteSpace: 'nowrap', fontSize: 12 }}>{o.signed ? 'Signed' : 'Not yet signed'}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <PdfViewer token={token} onReachedEnd={() => setReachedEnd(true)} />

      <Panel>
        <div style={{ fontSize: 16, marginBottom: 14 }}>Sign</div>
        {!reachedEnd && (
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, marginBottom: 16, color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={ackRead} onChange={e => setAckRead(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }} />
            Scroll to the end of the document, or tick here to confirm you have read the full document.
          </label>
        )}
        <fieldset disabled={!readOk} style={{ border: 'none', padding: 0, margin: 0, opacity: readOk ? 1 : 0.45 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
            <Field label="Full name"><input style={bigInput} value={name} onChange={e => setName(e.target.value)} autoComplete="name" /></Field>
            <Field label="Email address (the one this invitation was sent to)"><input style={bigInput} type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" inputMode="email" /></Field>
          </div>

          <SignatureInput mode={mode} setMode={setMode} typedSig={typedSig} setTypedSig={setTypedSig}
            padRef={padRef} onDrawnChange={setHasDrawn} inputStyle={bigInput} accent={PORTAL.orange} />

          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, margin: '18px 0', cursor: 'pointer', lineHeight: 1.5 }}>
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }} />
            {req.consentText}
          </label>

          {error && <div style={{ padding: '10px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

          <button type="button" onClick={submit} disabled={!canSubmit} style={{
            width: '100%', height: 46, border: 'none', borderRadius: 'var(--radius-md)', fontSize: 15,
            background: PORTAL.orange, color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5,
          }}>{submitting ? 'Submitting...' : 'Sign & Submit'}</button>
        </fieldset>
      </Panel>
    </div>
  );
}

function PdfViewer({ token, onReachedEnd }) {
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
        const res = await post('/api/sign/document', { token });
        if (!res.ok) throw new Error('load');
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(await res.arrayBuffer()) }).promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        const container = containerRef.current;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const cssWidth = container.clientWidth;
        for (let n = 1; n <= pdf.numPages && !cancelled; n++) {
          const page = await pdf.getPage(n);
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width; canvas.height = viewport.height;
          canvas.style.cssText = 'width:100%;display:block;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.12);margin-bottom:12px;';
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
        if (!cancelled) setStatus('ready');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (status !== 'ready' || !sentinelRef.current) return;
    const obs = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { onReachedEnd(); obs.disconnect(); }
    });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [status, onReachedEnd]);

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>
        {status === 'loading' && 'Loading document...'}
        {status === 'ready' && `Document · ${pageCount} page${pageCount === 1 ? '' : 's'}`}
        {status === 'error' && 'The document could not be displayed. Refresh the page to try again.'}
      </div>
      <div ref={containerRef} />
      <div ref={sentinelRef} style={{ height: 1 }} />
      {status === 'ready' && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'center' }}>End of document</div>}
    </div>
  );
}

const bigInput = {
  width: '100%', height: 44, padding: '0 12px', fontSize: 16, boxSizing: 'border-box',
  border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)',
  background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-primary)',
};

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}
